import React from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import { Button } from "../../components/ui/button";
import { Copy, Check, Download, Loader2, Plus } from "lucide-react";
import ProseScaleControl from "./ProseScaleControl";

const SectionActionBar = ({
  variant,
  copyText,
  onCopy,
  onDownload,
  onNewQuery,
  downloadingPdf,
  copiedId,
  proseScale,
  showNewQuery = false,
  disableExport = false,
}) => {
  const { t } = useLanguage();

  // B2C: a single consumer report. The backend export renders the same report
  // regardless of doc_type, so we always request "patient".
  const isReport = variant === "report";
  const copyLabel = isReport
    ? t("query.b2cCopyReport")
    : variant === "patient"
      ? t("query.copyPatientMessage")
      : t("query.copyClinicalSummary");
  const copyId = isReport ? "report" : variant === "patient" ? "patient" : "physician";
  const docType = "patient";
  const pdfLabel = t("query.formatPdf");
  const docxLabel = t("query.formatDocx");

  return (
    <div
      className="sticky top-14 md:top-16 z-10 -mx-4 md:-mx-5 px-4 md:px-5 py-2 bg-card/95 backdrop-blur border-b border-border"
      data-testid={`section-action-bar-${variant}`}
    >
      <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
        {proseScale && (
          <ProseScaleControl
            scale={proseScale.scale}
            onDecrease={proseScale.decrease}
            onIncrease={proseScale.increase}
            canDecrease={proseScale.canDecrease}
            canIncrease={proseScale.canIncrease}
          />
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => onCopy(copyText, copyId)}
          className="gap-1.5 h-8 text-xs focus-visible:ring-ring"
          data-testid={`section-copy-${variant}-btn`}
        >
          {copiedId === copyId ? (
            <Check className="w-3 h-3 text-primary" />
          ) : (
            <Copy className="w-3 h-3" />
          )}
          {copiedId === copyId ? t("query.copied") : copyLabel}
        </Button>

        {!disableExport && (
          <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDownload(docType, "pdf")}
          disabled={!!downloadingPdf}
          className="gap-1.5 h-8 text-xs focus-visible:ring-ring"
          aria-label={pdfLabel}
          data-testid={`section-pdf-${variant}-btn`}
        >
          {downloadingPdf === `${docType}-pdf` ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Download className="w-3.5 h-3.5" />
          )}
          {pdfLabel}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onDownload(docType, "docx")}
          disabled={!!downloadingPdf}
          className="gap-1.5 h-8 text-xs focus-visible:ring-ring"
          aria-label={docxLabel}
          data-testid={`section-docx-${variant}-btn`}
        >
          {downloadingPdf === `${docType}-docx` ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Download className="w-3.5 h-3.5" />
          )}
          {docxLabel}
        </Button>
          </>
        )}

        {showNewQuery && onNewQuery && (
          <>
            <div className="flex-1" />
            <Button
              size="sm"
              onClick={onNewQuery}
              className="gap-1.5 h-8 text-xs"
              data-testid="section-new-query-btn"
            >
              <Plus className="w-3.5 h-3.5" />
              {t("nav.newQuery")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SectionActionBar;
